class AnchorMenu extends HTMLElement {
  constructor() {
    super();

    this.toggleBtn = null;
    this.links = [];
    this.activeLabelEl = null;
    this.isOpen = false;
    this.observer = null;

    // Aggiunte per la chiusura cliccando fuori
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  connectedCallback() {
    const toggleSlot = this.querySelector('[slot="toggle"]');
    const linksSlot = this.querySelector('[slot="links"]');

    if (!toggleSlot || !linksSlot) {
      console.warn('AnchorMenu: missing slots "toggle" or "links"');
      return;
    }

    this.toggleBtn = toggleSlot;
    this.activeLabelEl = toggleSlot.querySelector('.anchor-menu__active-label');
    this.links = [...linksSlot.querySelectorAll('[data-anchor-target]')];

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleLinkClick = this.handleLinkClick.bind(this);
    this.handleIntersect = this.handleIntersect.bind(this);

    this.toggleBtn.addEventListener('click', this.handleToggleClick);
    this.links.forEach(link => {
      link.addEventListener('click', this.handleLinkClick);
    });

    // Aggiungo l'ascoltatore per il click sul documento
    document.addEventListener('click', this.handleOutsideClick);

    this.setupObserver();
  }

  disconnectedCallback() {
    // Rimuovo l'ascoltatore per evitare memory leak
    document.removeEventListener('click', this.handleOutsideClick);

    // Rimuovi eventuali altri listener se necessario
    if (this.toggleBtn) {
      this.toggleBtn.removeEventListener('click', this.handleToggleClick);
    }
    this.links.forEach(link => {
      link.removeEventListener('click', this.handleLinkClick);
    });
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * 1) Al click del toggle apro/chiudo il menu
   * 2) Se il menu si apre su mobile, eseguo uno scroll in alto
   *    in modo che l’elemento sia fissato al top (sticky).
   */
  handleToggleClick(e) {
    this.isOpen = !this.isOpen;
    this.toggleBtn.setAttribute('aria-expanded', this.isOpen);
    this.classList.toggle('is-open', this.isOpen);

    // Se il menu è appena stato aperto su mobile, forzo lo scroll in alto
    if (this.isOpen && window.innerWidth < 1025) {
      const rect = this.getBoundingClientRect();
      // Se top < 0 significa che è fuori dallo schermo verso l’alto,
      // oppure puoi gestire diversamente in base alla tua logica di layout
      if (rect.top > 14) {
        window.scrollBy({
          top: rect.top,
          behavior: 'smooth'
        });
      }
    }
  }

  /**
   * Chiusura del menu cliccando fuori (solo se è aperto).
   */
  handleOutsideClick(e) {
    // Se clicco fuori dall'elemento e il menu è aperto, lo chiudo
    if (this.isOpen && !this.contains(e.target)) {
      this.isOpen = false;
      this.toggleBtn.setAttribute('aria-expanded', false);
      this.classList.remove('is-open');
    }
  }

  /**
   * Gestione del click su un link interno:
   * - Aggiorna la label del toggle
   * - Chiude il menu
   * - Fa lo scroll verso la sezione
   */
  handleLinkClick(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const targetSelector = link.dataset.anchorTarget;
    const targetEl = document.querySelector(targetSelector);

    // (1) Aggiorna la label del bottone col testo del link cliccato
    if (this.activeLabelEl) {
      this.activeLabelEl.textContent = link.textContent;
    }

    // (2) Chiudi il menu PRIMA di scrollare
    this.isOpen = false;
    this.toggleBtn.setAttribute('aria-expanded', false);
    this.classList.remove('is-open');

    // (3) Effettua lo scroll verso la sezione
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: 'smooth' });
    }
  }

  setupObserver() {
    const sections = this.links
        .map(link => {
          const sel = link.dataset.anchorTarget;
          const el = document.querySelector(sel);
          return el ? { el, link } : null;
        })
        .filter(Boolean);

    if (!sections.length) return;

    this.observer = new IntersectionObserver(this.handleIntersect, {
      rootMargin: '-78px 0px -78px 0px',
      threshold: 0
    });

    sections.forEach(s => this.observer.observe(s.el));
  }

  handleIntersect(entries) {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Rimuovo "is-active" da tutti i link
        this.links.forEach(a => a.classList.remove('is-active'));

        // Trovo il link corrispondente
        const found = this.links.find(l => l.dataset.anchorTarget === `#${entry.target.id}`);
        if (found) {
          found.classList.add('is-active');

          // Se vuoi che la label cambi automaticamente all’osservazione della sezione,
          // lascialo. Se preferisci cambiarla solo al click, rimuovilo.
          if (this.activeLabelEl) {
            this.activeLabelEl.textContent = found.textContent;
          }
        }
      }
    });
  }
}

customElements.define('anchor-menu', AnchorMenu);
